import { buildReassignmentCaptureKey } from "../lib/dispatchCaptureArrivalHighlight.js";

export const COMBINED_CAPTURE_ACTIVITY_PAGE_SIZE = 25;
export const COMBINED_CAPTURE_IMPORT_BATCH_SIZE = 100;

const ACTIVITY_KIND_ORDER = {
  dispatch: 0,
  source_changes: 1,
  consolidation: 2,
  imports: 3,
  reports: 4,
  journal: 5,
};

const ACTIVITY_LABELS = {
  dispatch: "Activité Dispatch",
  source_changes: "Activité Mise à jour",
  consolidation: "Activité Consolidation",
  imports: "Import commandes",
  reports: "Rapport",
  journal: "Journal",
};

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveTimestampOrderValue(value) {
  const parsedTimestamp = Date.parse(normalizeText(value));
  return Number.isFinite(parsedTimestamp) ? parsedTimestamp : Number.NEGATIVE_INFINITY;
}

export function resolveDiagnosticActivityKind(item) {
  return item?.activityScope === "source_changes" || item?.activityScope === "consolidation" ? item.activityScope : "dispatch";
}

function buildImportActivityKey(item, index) {
  const auditId = normalizeText(item?.audit_id);
  if (auditId) {
    return `import|${auditId}`;
  }
  return [
    "import",
    normalizeText(item?.created_at),
    normalizeText(item?.source_filename),
    normalizeText(item?.action),
    String(index),
  ].join("|");
}

function buildReportActivityKey(item, index) {
  const auditId = normalizeText(item?.audit_id);
  if (auditId) {
    return `report|${auditId}`;
  }
  return [
    "report",
    normalizeText(item?.created_at),
    normalizeText(item?.action),
    String(index),
  ].join("|");
}

function compareCombinedCaptureActivityItems(left, right) {
  const leftTimestamp = resolveTimestampOrderValue(left?.occurredAt);
  const rightTimestamp = resolveTimestampOrderValue(right?.occurredAt);
  if (leftTimestamp !== rightTimestamp) {
    return rightTimestamp - leftTimestamp;
  }

  const kindDifference = (ACTIVITY_KIND_ORDER[left?.activityKind] ?? 99)
    - (ACTIVITY_KIND_ORDER[right?.activityKind] ?? 99);
  if (kindDifference !== 0) {
    return kindDifference;
  }
  return normalizeText(left?.key).localeCompare(normalizeText(right?.key));
}

export function buildCombinedCaptureActivityItems(diagnosticItems = [], importItems = [], reportItems = [], journalItems = []) {
  const combinedItems = [];
  const seenKeys = new Set();

  (Array.isArray(diagnosticItems) ? diagnosticItems : []).forEach((item, index) => {
    const activityKind = resolveDiagnosticActivityKind(item);
    const diagnosticKey = buildReassignmentCaptureKey(item) || `diagnostic|${activityKind}|${index}`;
    const key = `diagnostic|${activityKind}|${diagnosticKey}`;
    if (seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);
    combinedItems.push({
      activityKind,
      activityLabel: ACTIVITY_LABELS[activityKind],
      item,
      key,
      occurredAt: normalizeText(item?.recordedAt),
    });
  });

  (Array.isArray(importItems) ? importItems : []).forEach((item, index) => {
    const key = buildImportActivityKey(item, index);
    if (seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);
    combinedItems.push({
      activityKind: "imports",
      activityLabel: ACTIVITY_LABELS.imports,
      item,
      key,
      occurredAt: normalizeText(item?.created_at),
    });
  });

  (Array.isArray(reportItems) ? reportItems : []).forEach((item, index) => {
    const key = buildReportActivityKey(item, index);
    if (seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);
    combinedItems.push({
      activityKind: "reports",
      activityLabel: ACTIVITY_LABELS.reports,
      item,
      key,
      occurredAt: normalizeText(item?.created_at),
    });
  });

  (Array.isArray(journalItems) ? journalItems : []).forEach(item => {
    const key = `journal|${normalizeText(item?.audit_id)}`;
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    combinedItems.push({ activityKind: "journal", activityLabel: ACTIVITY_LABELS.journal,
      item, key, occurredAt: normalizeText(item?.created_at) });
  });
  return combinedItems.sort(compareCombinedCaptureActivityItems);
}

export function paginateCombinedCaptureActivityItems(items = [], {
  page = 1,
  pageSize = COMBINED_CAPTURE_ACTIVITY_PAGE_SIZE,
  total,
} = {}) {
  const normalizedItems = Array.isArray(items) ? items : [];
  const normalizedPageSize = Number.isInteger(Number(pageSize)) && Number(pageSize) > 0
    ? Number(pageSize)
    : COMBINED_CAPTURE_ACTIVITY_PAGE_SIZE;
  const normalizedTotal = Number.isFinite(Number(total)) && Number(total) >= 0
    ? Math.trunc(Number(total))
    : normalizedItems.length;
  const pageCount = Math.max(1, Math.ceil(normalizedTotal / normalizedPageSize));
  const requestedPage = Number.isInteger(Number(page)) && Number(page) > 0 ? Number(page) : 1;
  const currentPage = Math.min(requestedPage, pageCount);
  const startIndex = (currentPage - 1) * normalizedPageSize;

  return {
    currentPage,
    items: normalizedItems.slice(startIndex, startIndex + normalizedPageSize),
    pageCount,
    total: normalizedTotal,
  };
}

export function resolveRequiredImportBatchCount({
  importTotal,
  page = 1,
  pageSize = COMBINED_CAPTURE_ACTIVITY_PAGE_SIZE,
  batchSize = COMBINED_CAPTURE_IMPORT_BATCH_SIZE,
} = {}) {
  const normalizedPage = Number.isInteger(Number(page)) && Number(page) > 0 ? Number(page) : 1;
  const normalizedPageSize = Number.isInteger(Number(pageSize)) && Number(pageSize) > 0
    ? Number(pageSize)
    : COMBINED_CAPTURE_ACTIVITY_PAGE_SIZE;
  const normalizedBatchSize = Number.isInteger(Number(batchSize)) && Number(batchSize) > 0
    ? Number(batchSize)
    : COMBINED_CAPTURE_IMPORT_BATCH_SIZE;
  const normalizedImportTotal = Number.isFinite(Number(importTotal)) && Number(importTotal) >= 0
    ? Math.trunc(Number(importTotal))
    : normalizedPage * normalizedPageSize;
  const requiredImportCount = Math.min(normalizedPage * normalizedPageSize, normalizedImportTotal);

  return Math.max(1, Math.ceil(requiredImportCount / normalizedBatchSize));
}
